// hooks/useScrollRestoration.js
import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Persist and restore scroll position per location (back/forward = "POP").
 * @param {Object} opts
 * @param {string} [opts.selector]  - A CSS selector for a scrollable container. Omit to use window.
 * @param {number} [opts.offset=0]  - Extra offset (e.g., fixed header height).
 * @param {string} [opts.keySuffix] - Extra key suffix if multiple views share the same pathname.
 * If your page scrolls the window:
   import useScrollRestoration from "hooks/useScrollRestoration";

    const StoreMenu = ({ t }) => {
      // ...your existing code...
      useScrollRestoration({ offset: 60 }); // your fixed top bar is ~60px
      // ...return JSX...
    };

    If you scroll a container (e.g., .store-menu-list on the wrapper div):
    useScrollRestoration({ selector: ".store-menu-list", offset: 60 });
 */

export default function useScrollRestoration(opts = {}) {
  const { selector, offset = 0, keySuffix = "" } = opts;
  const location = useLocation();
  const navType = useNavigationType(); // "POP" on back/forward
  const key = `${location.pathname}${location.search}${keySuffix}`;
  const restoredRef = useRef(false);

  const getTarget = () => {
    if (!selector) return { el: window, isWindow: true };
    const el = document.querySelector(selector);
    return { el: el || window, isWindow: !el };
  };

  const read = () => {
    try {
      const raw = sessionStorage.getItem(`scroll:${key}`);
      return raw ? JSON.parse(raw) : { x: 0, y: 0 };
    } catch {
      return { x: 0, y: 0 };
    }
  };

  const save = () => {
    const { el, isWindow } = getTarget();
    const x = isWindow ? window.scrollX : el.scrollLeft;
    const y = isWindow ? window.scrollY : el.scrollTop;
    sessionStorage.setItem(`scroll:${key}`, JSON.stringify({ x, y }));
  };

  const restoreNow = () => {
    const { el, isWindow } = getTarget();
    const { x, y } = read();
    const top = Math.max(0, y - offset);
    if (isWindow) window.scrollTo({ left: x, top, behavior: "auto" });
    else el.scrollTo({ left: x, top, behavior: "auto" });
    restoredRef.current = true;
  };

  // Restore on real back/forward (POP)
  useLayoutEffect(() => {
    if (navType !== "POP") return;
    restoreNow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, navType, selector, offset]);

  useLayoutEffect(() => {
    // Restore on PUSH if someone asked for it via state
    if (navType === "PUSH" && location.state && location.state.__restoreScroll && !restoredRef.current) {
      restoreNow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, navType, selector, offset, location.state]);

  // Fallback restore after paint
  useEffect(() => {
    if (navType !== "POP" || restoredRef.current) return;
    const raf = requestAnimationFrame(() => {
      restoreNow();
      setTimeout(restoreNow, 50);
    });
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, navType, selector, offset]);

  // Save on unmount, route changes, and page hide/refresh
  useEffect(() => {
    const onBeforeUnload = () => save();
    const onPageHide = () => save();
    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      save();
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("pagehide", onPageHide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, selector, offset]);

  // Expose manual saver (use this before custom navigate)
  return { saveScroll: save, restoreScroll: restoreNow };
}




